import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: Data is considered fresh for this duration
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Cache time: Data stays in cache for this duration after components unmount
      gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
      
      // Retry configuration
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch configuration
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnReconnect: true,    // Refetch when reconnecting to the internet
      refetchOnMount: true,        // Refetch when component mounts
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
      retryDelay: 1000, // Wait 1 second before retrying
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Show React Query DevTools in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
};

// Export the query client for use in components that need direct access
export { queryClient };

// Helper function to prefetch queries
export const prefetchQuery = {
  userProfile: () =>
    queryClient.prefetchQuery({
      queryKey: ['user', 'profile'],
      queryFn: () => import('@/shared/api/endpoints').then(api => api.api.user.getProfile()),
    }),
    
  packages: () =>
    queryClient.prefetchQuery({
      queryKey: ['packages', 'list'],
      queryFn: () => import('@/shared/api/endpoints').then(api => api.api.package.getAll()),
    }),
    
  wallet: () =>
    queryClient.prefetchQuery({
      queryKey: ['wallet', 'balance'],
      queryFn: () => import('@/shared/api/endpoints').then(api => api.api.wallet.get()),
    }),
};

// Helper function to set query data
export const setQueryData = {
  userProfile: (data: any) =>
    queryClient.setQueryData(['user', 'profile'], data),
    
  wallet: (data: any) =>
    queryClient.setQueryData(['wallet', 'balance'], data),
    
  // Add notification to the list optimistically
  addNotification: (notification: any) =>
    queryClient.setQueryData(['notifications', 'list'], (old: any) => {
      if (!old?.data?.data) return old;
      return {
        ...old,
        data: {
          ...old.data,
          data: [notification, ...old.data.data],
        },
      };
    }),
};

// Global error handler for queries
queryClient.setMutationDefaults(['error'], {
  onError: (error: any) => {
    console.error('Mutation error:', error);
    
    // Handle specific error types
    if (error?.response?.status === 401) {
      // Redirect to login on authentication errors
      window.location.href = '/login';
    } else if (error?.response?.status === 403) {
      // Show access denied message
      console.error('Access denied');
    } else if (error?.response?.status >= 500) {
      // Show server error message
      console.error('Server error occurred');
    }
  },
});

export default QueryProvider;
